import { useState } from 'react';
import { Alert, Button, Divider, Form, Input, InputNumber, Select, Space, Switch, Typography } from 'antd';
import type { TariffFormValues, TariffComponentFormValue, TouPeriodFormValue } from '../utils/tariffForm';
import {
  defaultComponent,
  emptyTariffFormValues,
  isValidHhMm,
  isValidVigencia,
  TARIFF_CURRENCY,
  toAntdFieldPath,
  validateComponentSet,
  validateTariffFormValues,
  validateTouPeriodSet,
} from '../utils/tariffForm';
import { TARIFF_COMPONENT_LABELS, TARIFF_TYPE_LABELS } from '../utils/format';
import type { TariffComponentType, TariffType } from '@goelectric/shared-types';

const TYPE_OPTIONS = (['FLAT', 'TOU', 'IDLE_FEE'] as const).map((value) => ({
  value,
  label: TARIFF_TYPE_LABELS[value],
}));

const COMPONENT_OPTIONS = (
  ['ENERGY', 'SESSION', 'TIME', 'IDLE_FEE', 'FLAT'] as const
).map((value) => ({
  value,
  label: TARIFF_COMPONENT_LABELS[value],
}));

export interface TariffFormProps {
  /** Valores iniciales (edición); por defecto, alta FLAT en COP. */
  initialValues?: TariffFormValues;
  mode?: 'create' | 'edit';
  submitting?: boolean;
  onSubmit: (values: TariffFormValues) => void | Promise<void>;
  onCancel?: () => void;
}

/** Componente por defecto al agregar una fila, según el tipo de tarifa elegido. */
function componentTypeFor(type: TariffType): TariffComponentType {
  if (type === 'IDLE_FEE') return 'IDLE_FEE';
  if (type === 'TOU') return 'ENERGY';
  return 'FLAT';
}

/**
 * Formulario de alta/edición de Tarifas (US-2.4).
 *
 * - Selector de tipo FLAT/TOU/IDLE_FEE (en edición queda fijo: el PATCH no acepta `type`).
 * - Editor dinámico de componentes de costo y de periodos TOU.
 * - Moneda fija COP, vigencia y switch de marca para OCPI (sincronización diferida a US 5.2).
 * - Validación inline por tipo con mensajes en tuteo.
 */
export function TariffForm({
  initialValues,
  mode = 'create',
  submitting = false,
  onSubmit,
  onCancel,
}: TariffFormProps) {
  const [form] = Form.useForm<TariffFormValues>();
  const defaults = initialValues ?? emptyTariffFormValues();
  const type = Form.useWatch('type', form) ?? defaults.type;
  const isEdit = mode === 'edit';
  const [validationSummary, setValidationSummary] = useState<string[]>([]);

  /**
   * Red de seguridad final: las reglas inline dan feedback inmediato, pero acá se
   * vuelve a validar el modelo completo con `validateTariffFormValues` antes de
   * enviar. Si hay problemas, se mapean a paths de antd y no se llama a `onSubmit`.
   */
  const handleFinish = (values: TariffFormValues) => {
    const issues = validateTariffFormValues(values);
    if (issues.length > 0) {
      setValidationSummary(issues.map((issue) => issue.message));
      form.setFields(
        issues.map((issue) => ({
          name: toAntdFieldPath(issue.field),
          errors: [issue.message],
        })) as Parameters<typeof form.setFields>[0],
      );
      return;
    }

    setValidationSummary([]);
    void onSubmit({
      ...values,
      name: values.name?.trim() ?? '',
      description: values.description?.trim() ?? '',
      components: values.components ?? [],
      touPeriods: values.touPeriods ?? [],
    });
  };

  return (
    <Form<TariffFormValues>
      form={form}
      layout="vertical"
      initialValues={defaults}
      onFinish={handleFinish}
      onValuesChange={() => {
        if (validationSummary.length > 0) setValidationSummary([]);
      }}
      requiredMark={false}
      disabled={submitting}
    >
      <Typography.Title level={5} style={{ marginTop: 0 }}>
        Datos de la tarifa
      </Typography.Title>
      {validationSummary.length > 0 && (
        <Alert
          type="error"
          showIcon
          role="alert"
          aria-live="assertive"
          message="Revisa los datos de la tarifa"
          description={
            <ul style={{ margin: 0, paddingInlineStart: 20 }}>
              {validationSummary.map((message, index) => (
                <li key={`${index}-${message}`}>{message}</li>
              ))}
            </ul>
          }
          style={{ marginBottom: 16 }}
        />
      )}
      <Form.Item
        label="Nombre"
        name="name"
        rules={[{ required: true, whitespace: true, message: 'Escribe el nombre de la tarifa.' }]}
      >
        <Input placeholder="Ej. Tarifa nocturna CCS2" autoComplete="off" />
      </Form.Item>

      <Form.Item label="Descripción (opcional)" name="description">
        <Input.TextArea
          rows={2}
          maxLength={300}
          showCount
          placeholder="Cuéntale al equipo cuándo aplica esta tarifa."
        />
      </Form.Item>

      <Space size={16} wrap align="start">
        <Form.Item
          label="Tipo de tarifa"
          name="type"
          rules={[{ required: true, message: 'Elige el tipo de tarifa.' }]}
          style={{ minWidth: 220 }}
        >
          <Select<TariffType> options={TYPE_OPTIONS} disabled={isEdit} />
        </Form.Item>

        <Form.Item label="Moneda" htmlFor="tariff-currency">
          <Input
            id="tariff-currency"
            value={TARIFF_CURRENCY}
            readOnly
            aria-label="Moneda"
            style={{ width: 100 }}
          />
        </Form.Item>
      </Space>
      {isEdit ? (
        <Typography.Paragraph type="secondary" style={{ marginTop: -8 }}>
          El tipo de tarifa y la moneda no se pueden cambiar después de crear la tarifa.
        </Typography.Paragraph>
      ) : null}

      <Space size={16} wrap align="start">
        <Form.Item label="Vigente desde" name="validFrom" style={{ minWidth: 240 }}>
          <Input type="datetime-local" aria-label="Vigente desde" />
        </Form.Item>
        <Form.Item
          label="Vigente hasta (opcional)"
          name="validTo"
          dependencies={['validFrom']}
          style={{ minWidth: 240 }}
          rules={[
            {
              validator: async (_, value: string | undefined) => {
                const from = form.getFieldValue('validFrom') as string | undefined;
                if (!value || isValidVigencia(from, value)) return;
                throw new Error('La fecha de fin debe ser igual o posterior a la fecha de inicio.');
              },
            },
          ]}
        >
          <Input type="datetime-local" aria-label="Vigente hasta" />
        </Form.Item>
      </Space>

      <Form.Item
        label="Marcar para OCPI"
        name="publishedToOcpi"
        valuePropName="checked"
        extra="Queda marcada para publicarse cuando se habilite la integración OCPI (US 5.2). La sincronización aún no está implementada."
      >
        <Switch checkedChildren="Marcada" unCheckedChildren="No marcada" />
      </Form.Item>

      <Divider />

      <Typography.Title level={5}>Componentes de costo</Typography.Title>
      <Typography.Paragraph type="secondary">
        Define al menos un componente con su precio en COP y el IVA aplicable. El precio es por
        kWh, por minuto o un cargo fijo según el tipo.
      </Typography.Paragraph>

      <Form.List
        name="components"
        rules={[
          {
            validator: async (_, components: TariffComponentFormValue[] | undefined) => {
              const message = validateComponentSet(type, components ?? []);
              if (message) throw new Error(message);
            },
          },
        ]}
      >
        {(fields, { add, remove }, { errors }) => (
          <>
            {fields.map((field, index) => (
              <Space key={field.key} align="start" wrap style={{ marginBottom: 8 }}>
                <Form.Item
                  label={index === 0 ? 'Tipo' : ' '}
                  name={[field.name, 'type']}
                  rules={[{ required: true, message: 'Elige el tipo de componente.' }]}
                >
                  <Select<TariffComponentType>
                    options={COMPONENT_OPTIONS}
                    style={{ width: 140 }}
                    aria-label={`Tipo del componente ${index + 1}`}
                  />
                </Form.Item>
                <Form.Item
                  label={index === 0 ? 'Precio (COP)' : ' '}
                  name={[field.name, 'priceCop']}
                  rules={[
                    { required: true, message: 'Ingresa el precio del componente.' },
                    {
                      type: 'number',
                      min: 0,
                      message: 'El precio debe ser mayor o igual a 0.',
                    },
                  ]}
                >
                  <InputNumber
                    min={0}
                    step={50}
                    style={{ width: 150 }}
                    aria-label={`Precio del componente ${index + 1} (COP)`}
                    placeholder="1150"
                  />
                </Form.Item>
                <Form.Item
                  label={index === 0 ? 'IVA (%)' : ' '}
                  name={[field.name, 'vatRatePct']}
                  rules={[
                    { required: true, message: 'Ingresa el IVA del componente.' },
                    {
                      type: 'number',
                      min: 0,
                      max: 100,
                      message: 'El IVA debe estar entre 0 y 100.',
                    },
                  ]}
                >
                  <InputNumber
                    min={0}
                    max={100}
                    style={{ width: 110 }}
                    aria-label={`IVA del componente ${index + 1} (%)`}
                  />
                </Form.Item>
                <Form.Item
                  label={index === 0 ? 'Paso' : ' '}
                  name={[field.name, 'stepSize']}
                  rules={[
                    {
                      validator: async (_, value: number | null | undefined) => {
                        if (value === undefined || value === null || value >= 0.001) return;
                        throw new Error('El tamaño del paso debe ser mayor o igual a 0.001.');
                      },
                    },
                  ]}
                >
                  <InputNumber
                    min={0.001}
                    step={0.001}
                    precision={3}
                    style={{ width: 110 }}
                    aria-label={`Paso del componente ${index + 1}`}
                    placeholder="1"
                  />
                </Form.Item>
                <Form.Item label={index === 0 ? 'Descripción' : ' '} name={[field.name, 'description']}>
                  <Input
                    style={{ width: 200 }}
                    aria-label={`Descripción del componente ${index + 1}`}
                    placeholder="Opcional"
                  />
                </Form.Item>
                <Form.Item label=" ">
                  <Button
                    danger
                    type="text"
                    onClick={() => remove(field.name)}
                    aria-label={`Eliminar componente ${index + 1}`}
                  >
                    Quitar
                  </Button>
                </Form.Item>
              </Space>
            ))}

            <Form.Item>
              <Button
                type="dashed"
                onClick={() => add(defaultComponent(componentTypeFor(type)))}
                block
              >
                Agregar componente
              </Button>
              <Form.ErrorList errors={errors} />
            </Form.Item>
          </>
        )}
      </Form.List>

      {type === 'TOU' ? (
        <>
          <Divider />
          <Typography.Title level={5}>Periodos TOU (pico-valle)</Typography.Title>
          <Typography.Paragraph type="secondary">
            Agrega al menos un periodo con hora de inicio y fin en formato HH:mm y su precio por
            kWh. Las horas son locales de la ubicación.
          </Typography.Paragraph>

          <Form.List
            name="touPeriods"
            rules={[
              {
                validator: async (_, periods: TouPeriodFormValue[] | undefined) => {
                  const message = validateTouPeriodSet(type, periods ?? []);
                  if (message) throw new Error(message);
                },
              },
            ]}
          >
            {(fields, { add, remove }, { errors }) => (
              <>
                {fields.map((field, index) => (
                  <Space key={field.key} align="start" wrap style={{ marginBottom: 8 }}>
                    <Form.Item
                      label={index === 0 ? 'Nombre' : ' '}
                      name={[field.name, 'label']}
                      rules={[{ required: true, whitespace: true, message: 'Escribe un nombre para el periodo.' }]}
                    >
                      <Input
                        style={{ width: 140 }}
                        aria-label={`Nombre del periodo ${index + 1}`}
                        placeholder="Valle"
                      />
                    </Form.Item>
                    <Form.Item
                      label={index === 0 ? 'Inicio' : ' '}
                      name={[field.name, 'startTime']}
                      rules={[
                        { required: true, message: 'Ingresa la hora de inicio.' },
                        {
                          validator: async (_, value: string | undefined) => {
                            if (!value || isValidHhMm(value)) return;
                            throw new Error('Usa el formato HH:mm.');
                          },
                        },
                      ]}
                    >
                      <Input
                        type="time"
                        style={{ width: 130 }}
                        aria-label={`Hora de inicio del periodo ${index + 1}`}
                      />
                    </Form.Item>
                    <Form.Item
                      label={index === 0 ? 'Fin' : ' '}
                      name={[field.name, 'endTime']}
                      dependencies={[['touPeriods', field.name, 'startTime']]}
                      rules={[
                        { required: true, message: 'Ingresa la hora de fin.' },
                        {
                          validator: async (_, value: string | undefined) => {
                            if (!value) return;
                            if (!isValidHhMm(value)) {
                              throw new Error('Usa el formato HH:mm.');
                            }
                            const startTime = form.getFieldValue([
                              'touPeriods',
                              field.name,
                              'startTime',
                            ]) as string | undefined;
                            if (startTime && startTime === value) {
                              throw new Error(
                                'La hora de fin debe ser distinta de la hora de inicio.',
                              );
                            }
                          },
                        },
                      ]}
                    >
                      <Input
                        type="time"
                        style={{ width: 130 }}
                        aria-label={`Hora de fin del periodo ${index + 1}`}
                      />
                    </Form.Item>
                    <Form.Item
                      label={index === 0 ? 'Precio (COP/kWh)' : ' '}
                      name={[field.name, 'priceCopPerKwh']}
                      rules={[
                        { required: true, message: 'Ingresa el precio por kWh del periodo.' },
                        {
                          type: 'number',
                          min: 0,
                          message: 'El precio por kWh debe ser mayor o igual a 0.',
                        },
                      ]}
                    >
                      <InputNumber
                        min={0}
                        step={50}
                        style={{ width: 160 }}
                        aria-label={`Precio por kWh del periodo ${index + 1}`}
                        placeholder="850"
                      />
                    </Form.Item>
                    <Form.Item label=" ">
                      <Button
                        danger
                        type="text"
                        onClick={() => remove(field.name)}
                        aria-label={`Eliminar periodo ${index + 1}`}
                      >
                        Quitar
                      </Button>
                    </Form.Item>
                  </Space>
                ))}

                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() => add({ label: '', startTime: '', endTime: '', priceCopPerKwh: null })}
                    block
                  >
                    Agregar periodo
                  </Button>
                  <Form.ErrorList errors={errors} />
                </Form.Item>
              </>
            )}
          </Form.List>
        </>
      ) : null}

      <Divider />

      <Space>
        <Button type="primary" htmlType="submit" loading={submitting}>
          {isEdit ? 'Guardar cambios' : 'Crear tarifa'}
        </Button>
        {onCancel && (
          <Button onClick={onCancel} disabled={submitting}>
            Cancelar
          </Button>
        )}
      </Space>
    </Form>
  );
}
